import * as Notifications from 'expo-notifications';
import { SchedulableTriggerInputTypes } from 'expo-notifications';
import { Platform } from 'react-native';
import type { LocationCoordinates } from './location';
import { isWithinRadius } from './location';
import type { Place } from './places';
import type { NavigationPrompt } from './navigation';
import type { ItineraryItem } from './itinerary';
import type { DayBriefing } from './dayBriefing';
import { sendGatedNotification, DEFAULT_COOLDOWNS } from './notificationGate';
import { buildDedupeKey } from './notificationDedupe';
import { toLocalDateKey } from '@/utils/dateKeys';

// Configure notification handler
Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldShowBanner: true,
    shouldShowList: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
  }),
});

const NOTIFICATION_PERMISSION_CACHE_TTL_MS = 30_000;

type CachedPermission = {
  granted: boolean;
  expiresAtMs: number;
};

let cachedPermission: CachedPermission | null = null;
let permissionRequestInFlight: Promise<boolean> | null = null;
let androidChannelsConfigured = false;

const getCachedPermission = (): boolean | null => {
  if (!cachedPermission) return null;
  if (Date.now() > cachedPermission.expiresAtMs) {
    cachedPermission = null;
    return null;
  }
  return cachedPermission.granted;
};

const setCachedPermission = (granted: boolean, ttlMs = NOTIFICATION_PERMISSION_CACHE_TTL_MS) => {
  cachedPermission = {
    granted,
    expiresAtMs: Date.now() + ttlMs,
  };
};

const ensureDefaultAndroidChannelsAsync = async () => {
  if (Platform.OS !== 'android') return;
  if (androidChannelsConfigured) return;
  await Notifications.setNotificationChannelAsync('default', {
    name: 'Default',
    importance: Notifications.AndroidImportance.MAX,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: '#FF231F7C',
  });

  await Notifications.setNotificationChannelAsync('navigation', {
    name: 'Navigation',
    importance: Notifications.AndroidImportance.HIGH,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: '#FF231F7C',
  });

  await Notifications.setNotificationChannelAsync('landmarks', {
    name: 'Landmarks',
    importance: Notifications.AndroidImportance.HIGH,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: '#FF231F7C',
  });
  androidChannelsConfigured = true;
};

/**
 * Request notification permissions
 */
export const requestNotificationPermissions = async (): Promise<boolean> => {
  const cached = getCachedPermission();
  if (cached !== null) {
    return cached;
  }
  if (permissionRequestInFlight) {
    return permissionRequestInFlight;
  }

  permissionRequestInFlight = (async () => {
  try {
    const { status: existingStatus } = await Notifications.getPermissionsAsync();
    let finalStatus = existingStatus;

    if (existingStatus !== 'granted') {
      const { status } = await Notifications.requestPermissionsAsync();
      finalStatus = status;
    }

    if (finalStatus !== 'granted') {
      console.warn('[Notifications] Permission not granted');
      setCachedPermission(false);
      return false;
    }

    await ensureDefaultAndroidChannelsAsync();

    setCachedPermission(true);
    return true;
  } catch (error) {
    console.error('[Notifications] Error requesting permissions:', error);
    // Keep failure cache short to avoid hot loops if the OS/API is failing.
    setCachedPermission(false, 5_000);
    return false;
  } finally {
    permissionRequestInFlight = null;
  }
  })();

  return permissionRequestInFlight;
};

const dayBriefingIdByKey = new Map<string, string>();
const dayBriefingKeyById = new Map<string, string>();

export const scheduleDayBriefingNotification = async (
  briefing: DayBriefing,
  triggerDate: Date,
  options?: { skipPermissionCheck?: boolean },
): Promise<string | null> => {
  if (Platform.OS === 'web') {
    return null;
  }
  try {
    if (!options?.skipPermissionCheck) {
      const hasPermission = await requestNotificationPermissions();
      if (!hasPermission) return null;
    } else {
      await ensureDefaultAndroidChannelsAsync();
    }
    if (triggerDate.getTime() <= Date.now()) {
      return null;
    }

    const triggerDateKey = toLocalDateKey(triggerDate);
    const runtimeKey = `${briefing.tripId}:${triggerDateKey}`;
    const priorId = dayBriefingIdByKey.get(runtimeKey);
    if (priorId) {
      try {
        await Notifications.cancelScheduledNotificationAsync(priorId);
      } catch {
        // Best-effort cancel before rescheduling.
      }
      dayBriefingKeyById.delete(priorId);
    }

    try {
      const scheduled = await Notifications.getAllScheduledNotificationsAsync();
      await Promise.all(
        scheduled
          .filter((item: { identifier?: string; content?: { data?: Record<string, unknown> } }) => {
            const data = item.content?.data;
            return (
              data?.type === 'day_briefing' &&
              String(data.tripId) === briefing.tripId &&
              String(data.dateKey) === triggerDateKey &&
              Boolean(item.identifier)
            );
          })
          .map((item: { identifier?: string }) =>
            item.identifier
              ? Notifications.cancelScheduledNotificationAsync(item.identifier)
              : Promise.resolve(),
          ),
      );
    } catch {
      // Best-effort dedupe against stale OS-level schedules.
    }

    const lead = briefing.items[0];
    const body = lead?.detail
      ? `${lead.title}. ${lead.detail}`
      : lead?.title ?? briefing.trayLine;

    const id = await Notifications.scheduleNotificationAsync({
      content: {
        title: 'Your Tripvio day briefing',
        body,
        data: {
          type: 'day_briefing',
          tripId: briefing.tripId,
          dateKey: triggerDateKey,
          briefingDateKey: briefing.dayContext.todayKey,
        },
      },
      trigger: {
        type: SchedulableTriggerInputTypes.DATE,
        date: triggerDate,
      },
    });

    dayBriefingIdByKey.set(runtimeKey, id);
    dayBriefingKeyById.set(id, runtimeKey);
    return id;
  } catch (error) {
    console.error('[Notifications] Error scheduling day briefing:', error);
    return null;
  }
};

export const cancelDayBriefingNotification = async (notificationId: string): Promise<void> => {
  try {
    await Notifications.cancelScheduledNotificationAsync(notificationId);
    const runtimeKey = dayBriefingKeyById.get(notificationId);
    if (runtimeKey) {
      dayBriefingKeyById.delete(notificationId);
      if (dayBriefingIdByKey.get(runtimeKey) === notificationId) {
        dayBriefingIdByKey.delete(runtimeKey);
      }
    }
  } catch (error) {
    console.error('[Notifications] Error cancelling day briefing:', error);
  }
};

/**
 * Send navigation prompt notification
 */
export const sendNavigationPrompt = async (prompt: NavigationPrompt): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    const { nextActivity, fastestRoute, message, leaveInMinutes } = prompt;

    const durationText = formatDuration(fastestRoute.duration);
    const leaveText =
      leaveInMinutes > 0 ? `Leave in ${leaveInMinutes} min` : 'Leave now';
    const fullMessage = `${leaveText}. ${message} Fastest route: ${durationText} by ${fastestRoute.mode}.`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title: 'Time to Head Out! 🗺️',
        body: fullMessage,
        data: {
          type: 'navigation',
          activityId: nextActivity.id,
          tripId: nextActivity.tripId,
        },
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending navigation prompt:', error);
  }
};

/**
 * Notify user when they arrive at an itinerary stop.
 */
export const sendItineraryCheckInNotification = async (
  item: ItineraryItem,
  eventType: 'enter' | 'dwell'
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) return;

    const title = eventType === 'dwell'
      ? `Checked in: ${item.title}`
      : `Arrived: ${item.title}`;
    const locationLabel = item.address || item.location || 'this stop';

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body: `You're at ${locationLabel}. We've marked this stop as ${eventType === 'dwell' ? 'confirmed' : 'arrived'}.`,
        data: {
          type: 'itinerary_check_in',
          itemId: item.id,
          tripId: item.tripId,
          eventType,
        },
        priority: 'default',
      },
      trigger: null,
    });
  } catch (error) {
    console.error('[Notifications] Error sending itinerary check-in notification:', error);
  }
};

/**
 * Notify user when they arrive back at their base camp / accommodation.
 */
export const sendBaseCampArrivalNotification = async (params: {
  tripId: string;
  title: string;
  address?: string;
  eventType: 'enter' | 'dwell';
}): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) return;

    const body = params.address
      ? `You're at ${params.address}. We marked this as your base camp.`
      : 'You are back at your base camp.';

    await Notifications.scheduleNotificationAsync({
      content: {
        title: params.eventType === 'dwell' ? 'Welcome back' : 'You arrived',
        body,
        data: {
          type: 'base_camp_arrival',
          tripId: params.tripId,
          title: params.title,
        },
        priority: 'default',
      },
      trigger: null,
    });
  } catch (error) {
    console.error('[Notifications] Error sending base camp notification:', error);
  }
};

/**
 * Notify when the user arrives at a transportation hub (airport/train/bus).
 */
export const sendTransportHubArrivalNotification = async (params: {
  tripId?: string;
  hubName: string;
  hubType: 'airport' | 'train' | 'bus' | 'port' | 'other';
  eventType: 'enter' | 'dwell';
}): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) return;

    const emoji =
      params.hubType === 'airport'
        ? '✈️'
        : params.hubType === 'train'
          ? '🚆'
          : params.hubType === 'bus'
            ? '🚌'
            : '🧭';

    await Notifications.scheduleNotificationAsync({
      content: {
        title: `${emoji} You arrived at ${params.hubName}`,
        body: 'We will keep an eye on updates for this segment.',
        data: {
          type: 'transport_hub_arrival',
          tripId: params.tripId,
          hubName: params.hubName,
          hubType: params.hubType,
          eventType: params.eventType,
        },
        priority: 'high',
      },
      trigger: null,
    });
  } catch (error) {
    console.error('[Notifications] Error sending transport hub notification:', error);
  }
};

/**
 * Send landmark recommendation notification
 */
export const sendLandmarkNotification = async (
  place: Place,
  currentLocation: LocationCoordinates,
  hasNoActivity: boolean = false
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    const distance = Math.round(
      Math.sqrt(
        Math.pow(place.coordinates.latitude - currentLocation.latitude, 2) +
        Math.pow(place.coordinates.longitude - currentLocation.longitude, 2)
      ) * 111000 // Rough conversion to meters
    );

    let message = '';
    if (hasNoActivity) {
      message = `Looks like you're ${formatDistance(distance)} from ${place.name}. If you have no planned activity, check it out! Tap for directions.`;
    } else {
      message = `Just in case you're looking for a place to eat, here are some top rated spots you can check out. You're ${formatDistance(distance)} from ${place.name}.`;
    }

    await Notifications.scheduleNotificationAsync({
      content: {
        title: place.name,
        body: message,
        data: {
          type: 'landmark',
          placeId: place.id,
          coordinates: place.coordinates,
        },
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending landmark notification:', error);
  }
};

/**
 * Check for nearby landmarks and send notifications
 * Uses notification gate for preference/dedupe/rate-limit enforcement
 */
export const checkNearbyLandmarks = async (
  currentLocation: LocationCoordinates,
  landmarks: Place[],
  hasNoActivity: boolean = false
): Promise<void> => {
  try {
    const nearbyRadius = 500; // 500 meters
    const nearbyLandmarks = landmarks.filter((landmark) =>
      isWithinRadius(currentLocation, landmark.coordinates, nearbyRadius)
    );

    // Send notification for closest landmark
    if (nearbyLandmarks.length > 0) {
      // Sort by distance and get closest
      nearbyLandmarks.sort((a, b) => {
        const distA = Math.sqrt(
          Math.pow(a.coordinates.latitude - currentLocation.latitude, 2) +
          Math.pow(a.coordinates.longitude - currentLocation.longitude, 2)
        );
        const distB = Math.sqrt(
          Math.pow(b.coordinates.latitude - currentLocation.latitude, 2) +
          Math.pow(b.coordinates.longitude - currentLocation.longitude, 2)
        );
        return distA - distB;
      });

      const closestLandmark = nearbyLandmarks[0];
      
      // Use gated notification with preference/dedupe/rate-limit checks
      await sendGatedNotification(
        {
          category: 'itinerary',
          channel: 'push',
          priority: 'low',
          dedupeKey: buildDedupeKey.landmark(closestLandmark.id),
          cooldownMs: DEFAULT_COOLDOWNS.landmark,
        },
        () => sendLandmarkNotification(closestLandmark, currentLocation, hasNoActivity)
      );
    }
  } catch (error) {
    console.error('[Notifications] Error checking nearby landmarks:', error);
  }
};

/**
 * Format duration in seconds to human-readable string
 */
const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}hr ${minutes > 0 ? `${minutes}min` : ''}`;
  }
  return `${minutes}min`;
};

/**
 * Format distance in meters to human-readable string
 */
const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${meters}m`;
  }
  const km = (meters / 1000).toFixed(1);
  return `${km}km`;
};

/**
 * Cancel all scheduled notifications
 */
export const cancelAllNotifications = async (): Promise<void> => {
  try {
    await Notifications.cancelAllScheduledNotificationsAsync();
    await Notifications.dismissAllNotificationsAsync();
    await Notifications.setBadgeCountAsync(0);
  } catch (error) {
    console.error('[Notifications] Error cancelling notifications:', error);
  }
};

/**
 * Cancel all scheduled local notifications scoped to a trip.
 * This prevents stale reminders from firing after a trip is deleted.
 */
export const cancelNotificationsForTrip = async (tripId: string): Promise<void> => {
  if (!tripId) return;
  try {
    const scheduled = await Notifications.getAllScheduledNotificationsAsync();
    const matches = scheduled.filter((item: any) => {
      const data = item?.content?.data as Record<string, unknown> | undefined;
      const scopedTripId =
        typeof data?.tripId === 'string' || typeof data?.tripId === 'number'
          ? String(data.tripId)
          : '';
      return scopedTripId === tripId;
    });

    await Promise.all(
      matches.map((item: any) =>
        item?.identifier
          ? Notifications.cancelScheduledNotificationAsync(item.identifier)
          : Promise.resolve()
      )
    );
  } catch (error) {
    console.error('[Notifications] Error cancelling trip notifications:', error);
  }
};

/** Decode common HTML entities in advisory text so lock-screen previews stay readable. */
const decodeNotificationPlainText = (text: string): string => {
  if (!text) return '';
  const s = text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&#xa0;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/<[^>]+>/g, ' ');
  return s.replace(/\s+/g, ' ').trim();
};

/**
 * Send safety advisory notification
 */
export const sendSafetyAdvisoryNotification = async (
  title: string,
  message: string,
  severity: 'low' | 'medium' | 'high' | 'critical',
  advisoryId?: string
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    const cleanTitle = decodeNotificationPlainText(title);
    const cleanMessage = decodeNotificationPlainText(message);

    // Configure notification channel for Android based on severity
    if (Platform.OS === 'android') {
      const channelId = severity === 'critical' || severity === 'high' ? 'safety_critical' : 'safety';
      
      await Notifications.setNotificationChannelAsync(channelId, {
        name: severity === 'critical' || severity === 'high' ? 'Critical Safety Alerts' : 'Safety Alerts',
        importance: severity === 'critical' ? Notifications.AndroidImportance.MAX : Notifications.AndroidImportance.HIGH,
        vibrationPattern: severity === 'critical' ? [0, 500, 250, 500] : [0, 250, 250, 250],
        lightColor: '#FF231F7C',
      });
    }

    await Notifications.scheduleNotificationAsync({
      content: {
        title: severity === 'critical' ? `🚨 ${cleanTitle}` : `⚠️ ${cleanTitle}`,
        body: cleanMessage,
        data: {
          type: 'safety_advisory',
          severity,
          advisoryId,
        },
        priority: severity === 'critical' ? 'max' : severity === 'high' ? 'high' : 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending safety advisory notification:', error);
  }
};

type FlightNotificationBase = {
  tripId: string;
  bookingId: string;
  flightLabel?: string;
};

const ensureFlightChannelAsync = async () => {
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('flight_updates', {
      name: 'Flight Updates',
      importance: Notifications.AndroidImportance.HIGH,
      vibrationPattern: [0, 250, 250, 250],
    });
  }
};

export const sendFlightDelayNotification = async (
  params: FlightNotificationBase & { delayMinutes?: number }
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) return;

    await ensureFlightChannelAsync();

    const delayText = params.delayMinutes
      ? `${params.delayMinutes} min delay`
      : 'Delayed';

    await Notifications.scheduleNotificationAsync({
      content: {
        title: `✈️ ${params.flightLabel || 'Flight'} Delayed`,
        body: delayText,
        data: {
          type: 'flight_delay',
          tripId: params.tripId,
          bookingId: params.bookingId,
        },
        priority: 'high',
      },
      trigger: null,
    });
  } catch (error) {
    console.error('[Notifications] Error sending flight delay notification:', error);
  }
};

export const sendGateChangeNotification = async (
  params: FlightNotificationBase & { previousGate?: string; newGate?: string }
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) return;

    await ensureFlightChannelAsync();

    const gateText = `${params.previousGate || '?'} → ${params.newGate || '?'}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title: `🛫 Gate Change: ${params.flightLabel || 'Flight'}`,
        body: `Gate updated to ${gateText}`,
        data: {
          type: 'flight_gate_change',
          tripId: params.tripId,
          bookingId: params.bookingId,
        },
        priority: 'high',
      },
      trigger: null,
    });
  } catch (error) {
    console.error('[Notifications] Error sending gate change notification:', error);
  }
};

export const sendCheckInReminderNotification = async (
  params: FlightNotificationBase & { checkInLink?: string }
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) return;

    await ensureFlightChannelAsync();

    await Notifications.scheduleNotificationAsync({
      content: {
        title: `✅ Check-In Available: ${params.flightLabel || 'Flight'}`,
        body: 'Complete online check-in to save time at the airport.',
        data: {
          type: 'flight_checkin',
          tripId: params.tripId,
          bookingId: params.bookingId,
          checkInLink: params.checkInLink,
        },
        priority: 'default',
      },
      trigger: null,
    });
  } catch (error) {
    console.error('[Notifications] Error sending check-in reminder notification:', error);
  }
};

/**
 * Send emergency contact notification (when SOS is triggered)
 */
export const sendEmergencyContactNotification = async (
  contactName: string,
  userLocation?: { latitude: number; longitude: number }
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    let message = `${contactName} has triggered an emergency SOS call.`;
    if (userLocation) {
      message += ` Location: ${userLocation.latitude.toFixed(4)}, ${userLocation.longitude.toFixed(4)}`;
    }

    await Notifications.scheduleNotificationAsync({
      content: {
        title: '🚨 Emergency Alert',
        body: message,
        data: {
          type: 'emergency',
          location: userLocation,
        },
        priority: 'max',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending emergency contact notification:', error);
  }
};

/**
 * Send location sharing status notification
 */
export const sendLocationSharingNotification = async (
  isStarting: boolean,
  recipientCount: number,
  expiresAt?: Date
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    const message = isStarting
      ? `Location sharing started with ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}.`
      : 'Location sharing stopped.';

    await Notifications.scheduleNotificationAsync({
      content: {
        title: isStarting ? '📍 Location Sharing Active' : '📍 Location Sharing Stopped',
        body: message + (expiresAt ? ` Expires: ${expiresAt.toLocaleTimeString()}` : ''),
        data: {
          type: 'location_sharing',
          isActive: isStarting,
        },
        sound: false, // Less intrusive for status updates
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending location sharing notification:', error);
  }
};

/**
 * Send budget alert notification
 */
export const sendBudgetAlertNotification = async (
  tripName: string,
  tripId: string,
  thresholdPercent: number,
  spentAmount: string,
  budgetAmount: string,
  isOverBudget: boolean,
  priority: 'high' | 'medium' | 'low'
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android based on priority
    if (Platform.OS === 'android') {
      const channelId = priority === 'high' ? 'budget_critical' : 'budget';
      
      await Notifications.setNotificationChannelAsync(channelId, {
        name: priority === 'high' ? 'Critical Budget Alerts' : 'Budget Alerts',
        importance: priority === 'high' ? Notifications.AndroidImportance.HIGH : Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: priority === 'high' ? [0, 250, 250, 250] : [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    const title = isOverBudget
      ? `💰 Budget Exceeded: ${tripName}`
      : `💰 Budget Alert: ${tripName}`;
    
    const body = isOverBudget
      ? `You've spent ${spentAmount}, exceeding your budget of ${budgetAmount}. Consider adjusting your plans.`
      : `You've reached ${Math.round(thresholdPercent * 100)}% of your budget. Spent: ${spentAmount} of ${budgetAmount}.`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'budget_alert',
          tripId,
          thresholdPercent,
          isOverBudget,
          priority,
        },
        priority: priority === 'high' ? 'high' : 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending budget alert notification:', error);
  }
};

/**
 * @deprecated DO NOT USE - Local notifications go to the current device only!
 * This would notify the INVITER instead of the INVITEE.
 * Use Cloud Function `onCollaboratorAdded` for proper FCM notifications.
 */
export const sendCollaborationNotification = async (
  tripName: string,
  tripId: string,
  inviterName?: string,
  permission: 'view' | 'edit' | 'add_items' = 'add_items'
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('collaboration', {
        name: 'Collaboration',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    const permissionLabels: Record<string, string> = {
      view: 'view only',
      edit: 'edit',
      add_items: 'add items',
    };

    const title = inviterName 
      ? `✈️ ${inviterName} invited you to collaborate`
      : `✈️ New trip collaboration`;
    
    const body = inviterName
      ? `${inviterName} added you as a collaborator on "${tripName}" with ${permissionLabels[permission]} permission.`
      : `You've been added as a collaborator on "${tripName}" with ${permissionLabels[permission]} permission.`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'collaboration',
          tripId,
          permission,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending collaboration notification:', error);
  }
};

/**
 * @deprecated DO NOT USE - Local notifications go to the current device only!
 * This would notify the ACTOR instead of the COLLABORATORS.
 * Deferred post-launch: implement Cloud Function + FCM cross-device notifications.
 * Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
 */
export const sendExpenseAddedNotification = async (
  tripName: string,
  tripId: string,
  actorName: string,
  expenseDescription: string,
  amount: number,
  currency: string,
  collaboratorUserIds: string[],
  actorUserId: string
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('collaboration', {
        name: 'Collaboration',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    // Filter out the actor from recipients
    const recipients = collaboratorUserIds.filter(id => id !== actorUserId);
    if (recipients.length === 0) return;

    const title = `💰 New expense on "${tripName}"`;
    const body = `${actorName} added an expense: ${expenseDescription || 'Untitled'} (${currency} ${amount.toFixed(2)})`;

    // Note: In a real implementation, you'd send individual notifications to each recipient
    // For now, we'll send a single notification (expo-notifications doesn't support targeting specific users)
    // In production, you'd use a push notification service like FCM
    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'expense_added',
          tripId,
          actorName,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending expense added notification:', error);
  }
};

/**
 * @deprecated DO NOT USE - Local notifications go to the current device only!
 * This would notify the ACTOR instead of the COLLABORATORS.
 * Deferred post-launch: implement Cloud Function + FCM cross-device notifications.
 * Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
 */
export const sendExpenseEditedNotification = async (
  tripName: string,
  tripId: string,
  actorName: string,
  expenseDescription: string,
  collaboratorUserIds: string[],
  actorUserId: string
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('collaboration', {
        name: 'Collaboration',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    // Filter out the actor from recipients
    const recipients = collaboratorUserIds.filter(id => id !== actorUserId);
    if (recipients.length === 0) return;

    const title = `✏️ Expense updated on "${tripName}"`;
    const body = `${actorName} updated an expense: ${expenseDescription || 'Untitled'}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'expense_edited',
          tripId,
          actorName,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending expense edited notification:', error);
  }
};

/**
 * Send document expiration notification
 */
export const sendDocumentExpirationNotification = async (
  documentName: string,
  documentType: string,
  daysUntil: number,
  isExpired: boolean,
  priority: 'high' | 'medium' | 'low'
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android based on priority
    if (Platform.OS === 'android') {
      const channelId = priority === 'high' ? 'documents_critical' : 'documents';
      
      await Notifications.setNotificationChannelAsync(channelId, {
        name: priority === 'high' ? 'Critical Document Alerts' : 'Document Alerts',
        importance: priority === 'high' ? Notifications.AndroidImportance.HIGH : Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: priority === 'high' ? [0, 250, 250, 250] : [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    const title = isExpired
      ? `⚠️ Document Expired: ${documentName}`
      : daysUntil <= 30
      ? `⚠️ Document Expiring Soon: ${documentName}`
      : `📄 Document Expiring: ${documentName}`;
    
    const body = isExpired
      ? `Your ${documentType} has expired. Please renew it to avoid travel disruptions.`
      : daysUntil <= 30
      ? `Your ${documentType} expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}. Renew it soon!`
      : `Your ${documentType} expires in ${daysUntil} days. Consider renewing it.`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'document_expiration',
          documentName,
          documentType,
          daysUntil,
          isExpired,
          priority,
        },
        priority: priority === 'high' ? 'high' : 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending document expiration notification:', error);
  }
};

/**
 * @deprecated DO NOT USE - Local notifications go to the current device only!
 * This would notify the ACTOR instead of the COLLABORATORS.
 * Deferred post-launch: implement Cloud Function + FCM cross-device notifications.
 * Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
 */
export const sendSplitNotification = async (
  tripName: string,
  tripId: string,
  actorName: string,
  expenseDescription: string,
  collaboratorUserIds: string[],
  actorUserId: string,
  isEdit: boolean = false
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('collaboration', {
        name: 'Collaboration',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    // Filter out the actor from recipients
    const recipients = collaboratorUserIds.filter(id => id !== actorUserId);
    if (recipients.length === 0) return;

    const title = isEdit 
      ? `💸 Split updated on "${tripName}"`
      : `💸 Expense split on "${tripName}"`;
    const body = `${actorName} ${isEdit ? 'updated' : 'added'} a split for: ${expenseDescription || 'Untitled'}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: isEdit ? 'split_edited' : 'split_added',
          tripId,
          actorName,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending split notification:', error);
  }
};

/**
 * @deprecated DO NOT USE - Local notifications go to the current device only!
 * This would notify the COMMENTER instead of the COLLABORATORS.
 * Deferred post-launch: implement Cloud Function + FCM cross-device notifications.
 * Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
 */
export const sendCommentNotification = async (
  tripName: string,
  tripId: string,
  actorName: string,
  commentPreview: string,
  collaboratorUserIds: string[],
  actorUserId: string
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('collaboration', {
        name: 'Collaboration',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    // Filter out the actor from recipients
    const recipients = collaboratorUserIds.filter(id => id !== actorUserId);
    if (recipients.length === 0) return;

    const title = `💬 New comment on "${tripName}"`;
    const preview = commentPreview.length > 50 ? commentPreview.substring(0, 50) + '...' : commentPreview;
    const body = `${actorName}: ${preview}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'comment_added',
          tripId,
          actorName,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending comment notification:', error);
  }
};

/**
 * @deprecated DO NOT USE - Local notifications go to the current device only!
 * This would notify the NUDGER instead of the DEBTOR.
 * Use Cloud Function `onSettlementNudgeCreated` for email notifications.
 * Deferred post-launch: extend Cloud Function to also send FCM push notifications.
 * Tracking: docs/PRELAUNCH_DEFERRED_ITEMS.md#notifications-cross-device-fcm
 */
export const sendSettlementNudgeNotification = async (
  tripName: string,
  tripId: string,
  fromUserName: string,
  toUserId: string,
  amount: number,
  currency: string
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('collaboration', {
        name: 'Collaboration',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    const title = `💸 Payment reminder for "${tripName}"`;
    const body = `${fromUserName} is reminding you about a payment of ${currency} ${amount.toFixed(2)}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'settlement_nudge',
          tripId,
          fromUserName,
          toUserId,
          amount,
          currency,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending settlement nudge notification:', error);
  }
};

/**
 * Send weather alert notification
 */
export const sendWeatherAlertNotification = async (
  tripName: string,
  tripId: string,
  destination: string,
  alertTitle: string,
  alertDescription: string,
  severity: 'low' | 'medium' | 'high' | 'critical'
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('weather', {
        name: 'Weather Alerts',
        importance: severity === 'critical' || severity === 'high' 
          ? Notifications.AndroidImportance.HIGH 
          : Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: severity === 'critical' ? [0, 500, 200, 500] : [0, 250, 250, 250],
        lightColor: '#FF231F7C',
      });
    }

    const emoji = severity === 'critical' ? '🚨' : severity === 'high' ? '⚠️' : '🌤️';
    const title = `${emoji} Weather Alert: ${tripName}`;
    const body = `${alertTitle} in ${destination}. ${alertDescription}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'weather_alert',
          tripId,
          destination,
          severity,
        },
        priority: severity === 'critical' || severity === 'high' ? 'high' : 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending weather alert notification:', error);
  }
};

/**
 * Send weather forecast notification (pre-trip)
 */
export const sendWeatherForecastNotification = async (
  tripName: string,
  tripId: string,
  destination: string,
  forecastSummary: string,
  daysUntilTrip: number
): Promise<void> => {
  try {
    const hasPermission = await requestNotificationPermissions();
    if (!hasPermission) {
      return;
    }

    // Configure notification channel for Android
    if (Platform.OS === 'android') {
      await Notifications.setNotificationChannelAsync('weather', {
        name: 'Weather Alerts',
        importance: Notifications.AndroidImportance.DEFAULT,
        vibrationPattern: [0, 200, 200],
        lightColor: '#FF231F7C',
      });
    }

    const title = `🌤️ Weather Forecast: ${tripName}`;
    const body = `${daysUntilTrip} day${daysUntilTrip !== 1 ? 's' : ''} until your trip to ${destination}. ${forecastSummary}`;

    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body,
        data: {
          type: 'weather_forecast',
          tripId,
          destination,
        },
        priority: 'default',
      },
      trigger: null, // Send immediately
    });
  } catch (error) {
    console.error('[Notifications] Error sending weather forecast notification:', error);
  }
};

const ensureTripReminderChannelAsync = async () => {
  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('trip_reminders', {
      name: 'Trip Reminders',
      importance: Notifications.AndroidImportance.DEFAULT,
      vibrationPattern: [0, 200, 200],
      lightColor: '#FF231F7C',
    });
  }
};

type SmartCueReminderParams = {
  tripId: string;
  cueId: string;
  cueKey?: string; // e.g. "missing_lodging@14" or "missing_lodging@immediate" (used for dedupe)
  title: string;
  body: string;
  triggerDate: Date;
};

type SmartCueReminderOptions = {
  // Caller has already done a permission check in the same flow.
  skipPermissionCheck?: boolean;
};

const smartCueIdByKey = new Map<string, string>();
const smartCueKeyById = new Map<string, string>();

/**
 * Schedule a reminder for a Smart Cue (local notification).
 * Returns the notification ID or null if not scheduled (e.g., past date or permission denied).
 */
export const scheduleSmartCueReminder = async (
  params: SmartCueReminderParams,
  options: SmartCueReminderOptions = {}
): Promise<string | null> => {
  try {
    // Local notifications are not supported on web; avoid hammering permission prompts
    if (Platform.OS === 'web') return null;

    if (!options.skipPermissionCheck) {
      const hasPermission = await requestNotificationPermissions();
      if (!hasPermission) return null;
    }

    // Ignore past triggers
    if (params.triggerDate.getTime() <= Date.now()) {
      return null;
    }

    await ensureTripReminderChannelAsync();

    // Lightweight dedupe: cancel any key we scheduled in this runtime before re-scheduling.
    // Persistent dedupe is handled by caller-level storage in useSmartCueNotifications.
    if (params.cueKey) {
      const dedupeKey = `${params.tripId}:${params.cueKey}`;
      const priorId = smartCueIdByKey.get(dedupeKey);
      if (priorId) {
        try {
          await Notifications.cancelScheduledNotificationAsync(priorId);
        } catch {
          // best-effort
        }
        smartCueKeyById.delete(priorId);
      }
    }

    const id = await Notifications.scheduleNotificationAsync({
      content: {
        title: params.title,
        body: params.body,
        data: {
          type: 'smart_cue',
          tripId: params.tripId,
          cueId: params.cueId,
          cueKey: params.cueKey || undefined,
        },
        priority: 'default',
      },
      // Expo SDK 52+: date triggers must use the object form (passing Date directly is deprecated).
      trigger: { type: SchedulableTriggerInputTypes.DATE, date: params.triggerDate },
    });

    if (params.cueKey) {
      const dedupeKey = `${params.tripId}:${params.cueKey}`;
      smartCueIdByKey.set(dedupeKey, id);
      smartCueKeyById.set(id, dedupeKey);
    }

    return id;
  } catch (error) {
    console.error('[Notifications] Error scheduling smart cue reminder:', error);
    return null;
  }
};

export const cancelSmartCueReminder = async (notificationId: string): Promise<void> => {
  try {
    await Notifications.cancelScheduledNotificationAsync(notificationId);
    const dedupeKey = smartCueKeyById.get(notificationId);
    if (dedupeKey) {
      smartCueKeyById.delete(notificationId);
      if (smartCueIdByKey.get(dedupeKey) === notificationId) {
        smartCueIdByKey.delete(dedupeKey);
      }
    }
  } catch (error) {
    console.error('[Notifications] Error cancelling smart cue reminder:', error);
  }
};

